import React, { useState, useEffect, useCallback } from 'react';
import { Clock, Edit3, Save, X, ChevronLeft, ChevronRight, AlertTriangle, MapPin, Shield, Plus } from 'lucide-react';
import { TimeEntryEditDialog } from '@/components/timeclock/TimeEntryEditDialog';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { API } from '@/utils/api';
import axios from 'axios';
import AdminLayout from '@/components/admin/AdminLayout';
import { useTranslation } from 'react-i18next';

const AdminTimeclock = ({ auth, onLogout }) => {
  const { t } = useTranslation();
  const [tab, setTab] = useState('entries');
  const [entries, setEntries] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedDate, setSelectedDate] = useState(() => new URLSearchParams(window.location.search).get('date') || new Date().toISOString().split('T')[0]);
  const [dialogEmp, setDialogEmp] = useState(null);
  const [schedule, setSchedule] = useState(null);
  const [editingEntry, setEditingEntry] = useState(null);
  const [editData, setEditData] = useState({});
  const [logs, setLogs] = useState([]);
  const [saving, setSaving] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [empRes, entryRes] = await Promise.all([
        axios.get(`${API}/employees`),
        axios.get(`${API}/time/all?start_date=${selectedDate}&end_date=${selectedDate}`),
      ]);
      setEmployees(empRes.data.filter(e => e.is_active));
      setEntries(entryRes.data);
    } catch {}
  }, [selectedDate]);

  const loadSchedule = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/timeclock/schedule`);
      setSchedule(res.data);
    } catch {}
  }, []);

  const loadLogs = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/timeclock/logs?date=${selectedDate}&limit=50`);
      setLogs(res.data);
    } catch {}
  }, [selectedDate]);

  useEffect(() => { loadData(); loadLogs(); }, [loadData, loadLogs]);
  useEffect(() => { loadSchedule(); }, [loadSchedule]);

  const shiftDate = (days) => {
    const d = new Date(selectedDate);
    d.setDate(d.getDate() + days);
    setSelectedDate(d.toISOString().split('T')[0]);
  };

  const getEntryForEmployee = (empId) => entries.find(e => e.employee_id === empId);

  const fmtTime = (iso) => {
    if (!iso) return '--:--';
    return new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  };

  const fmtTimeInput = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return `${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
  };

  const timeInputToISO = (timeStr, dateStr) => {
    if (!timeStr) return null;
    return `${dateStr}T${timeStr}:00`;
  };

  const startEdit = (emp, entry) => {
    setEditingEntry(emp.id);
    setEditData({
      clock_in: fmtTimeInput(entry?.clock_in),
      clock_out: fmtTimeInput(entry?.clock_out),
      breaks: (entry?.breaks || []).map(b => ({
        start_time: fmtTimeInput(b.start_time),
        end_time: fmtTimeInput(b.end_time),
        duration_minutes: b.duration_minutes,
      })),
    });
  };

  const saveEdit = async (empId) => {
    setSaving(true);
    try {
      const payload = {};
      if (editData.clock_in) payload.clock_in = timeInputToISO(editData.clock_in, selectedDate);
      if (editData.clock_out) payload.clock_out = timeInputToISO(editData.clock_out, selectedDate);
      if (editData.breaks) {
        payload.breaks = editData.breaks.map(b => {
          const start = timeInputToISO(b.start_time, selectedDate);
          const end = b.end_time ? timeInputToISO(b.end_time, selectedDate) : null;
          let duration = b.duration_minutes;
          if (start && end) {
            duration = Math.round((new Date(end) - new Date(start)) / 60000);
          }
          return { start_time: start, end_time: end, duration_minutes: duration };
        });
      }
      payload.status = payload.clock_out ? 'completed' : 'active';

      await axios.put(`${API}/timeclock/entries/${empId}/${selectedDate}`, payload);
      toast.success('Timbrage corrige');
      setEditingEntry(null);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const saveSchedule = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/timeclock/schedule`, schedule);
      toast.success('Horaires sauvegardes');
    } catch (err) {
      toast.error('Erreur');
    } finally {
      setSaving(false);
    }
  };

  const [allowedIps, setAllowedIps] = useState([]);
  const [newIp, setNewIp] = useState('');

  const loadIps = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/timeclock/allowed-ips`);
      setAllowedIps(res.data.ips || []);
    } catch {}
  }, []);

  useEffect(() => { loadIps(); }, [loadIps]);

  const saveIps = async () => {
    try {
      await axios.put(`${API}/timeclock/allowed-ips`, { ips: allowedIps });
      toast.success('IPs autorisees mises a jour');
    } catch { toast.error('Erreur'); }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr + 'T12:00:00').toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  };

  return (
    <AdminLayout auth={auth} onLogout={onLogout}>
    <div className="space-y-6" data-testid="admin-timeclock-page">
      <h1 className="text-2xl font-bold text-slate-900">{t('timeclock.title')}</h1>
      {/* Tabs */}
      <div className="flex gap-2 flex-wrap">
        <Button variant={tab === 'entries' ? 'default' : 'outline'} size="sm" onClick={() => setTab('entries')} data-testid="tab-entries">
          <Clock className="w-4 h-4 mr-1" /> Timbrages
        </Button>
        <Button variant={tab === 'schedule' ? 'default' : 'outline'} size="sm" onClick={() => setTab('schedule')} data-testid="tab-schedule">
          <Edit3 className="w-4 h-4 mr-1" /> Horaires
        </Button>
        <Button variant={tab === 'logs' ? 'default' : 'outline'} size="sm" onClick={() => setTab('logs')} data-testid="tab-logs">
          <Shield className="w-4 h-4 mr-1" /> Logs securite
        </Button>
        <Button variant={tab === 'ips' ? 'default' : 'outline'} size="sm" onClick={() => setTab('ips')} data-testid="tab-ips">
          <MapPin className="w-4 h-4 mr-1" /> IPs autorisees
        </Button>
      </div>

      {/* === ENTRIES TAB === */}
      {tab === 'entries' && (
        <Card className="p-6 rounded-xl border border-slate-200">
          <div className="flex items-center justify-between mb-4">
            <Button variant="ghost" size="sm" onClick={() => shiftDate(-1)} data-testid="prev-day">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <div className="text-center">
              <Input type="date" value={selectedDate} onChange={e => setSelectedDate(e.target.value)} className="text-center border-0 font-semibold text-lg" data-testid="date-picker" />
              <p className="text-xs text-slate-500 capitalize">{formatDate(selectedDate)}</p>
            </div>
            <Button variant="ghost" size="sm" onClick={() => shiftDate(1)} data-testid="next-day">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-2 px-2 font-semibold text-slate-600">Employe</th>
                  <th className="text-center py-2 px-2 font-semibold text-slate-600">Entree</th>
                  <th className="text-center py-2 px-2 font-semibold text-slate-600">Pauses</th>
                  <th className="text-center py-2 px-2 font-semibold text-slate-600">Sortie</th>
                  <th className="text-center py-2 px-2 font-semibold text-slate-600">Total</th>
                  <th className="text-center py-2 px-2 font-semibold text-slate-600">GPS</th>
                  <th className="text-center py-2 px-2 font-semibold text-slate-600">{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {employees.map(emp => {
                  const entry = getEntryForEmployee(emp.id);
                  const isEditing = editingEntry === emp.id;

                  return (
                    <tr key={emp.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`entry-row-${emp.id}`}>
                      <td className="py-2 px-2">
                        <p className="font-medium">{emp.first_name} {emp.last_name}</p>
                        <p className="text-xs text-slate-400">{emp.role}</p>
                      </td>
                      <td className="text-center py-2 px-2 font-mono">
                        {isEditing ? (
                          <Input type="time" value={editData.clock_in || ''} onChange={e => setEditData(p => ({...p, clock_in: e.target.value}))} className="w-24 mx-auto text-center text-sm" data-testid="edit-clock-in" />
                        ) : (
                          <span className={entry?.clock_in ? 'text-emerald-600 font-semibold' : 'text-slate-300'}>{entry ? fmtTime(entry.clock_in) : '—'}</span>
                        )}
                      </td>
                      <td className="text-center py-2 px-2 text-xs">
                        {isEditing ? (
                          <div className="space-y-1">
                            {(editData.breaks || []).map((b, i) => (
                              <div key={i} className="flex gap-1 items-center justify-center">
                                <Input type="time" value={b.start_time || ''} onChange={e => { const brks = [...editData.breaks]; brks[i].start_time = e.target.value; setEditData(p => ({...p, breaks: brks})); }} className="w-20 text-xs" />
                                <span>-</span>
                                <Input type="time" value={b.end_time || ''} onChange={e => { const brks = [...editData.breaks]; brks[i].end_time = e.target.value; setEditData(p => ({...p, breaks: brks})); }} className="w-20 text-xs" />
                              </div>
                            ))}
                          </div>
                        ) : (
                          entry?.breaks?.map((b, i) => (
                            <div key={i}>
                              {fmtTime(b.start_time)}-{b.end_time ? fmtTime(b.end_time) : '...'}{' '}
                              <span className={b.duration_minutes > 20 ? 'text-red-600 font-bold' : 'text-slate-500'}>
                                ({b.duration_minutes || '?'} min)
                              </span>
                            </div>
                          )) || <span className="text-slate-300">—</span>
                        )}
                      </td>
                      <td className="text-center py-2 px-2 font-mono">
                        {isEditing ? (
                          <Input type="time" value={editData.clock_out || ''} onChange={e => setEditData(p => ({...p, clock_out: e.target.value}))} className="w-24 mx-auto text-center text-sm" data-testid="edit-clock-out" />
                        ) : (
                          <span className={entry?.clock_out ? 'text-red-600 font-semibold' : 'text-slate-300'}>{entry ? fmtTime(entry.clock_out) : '—'}</span>
                        )}
                      </td>
                      <td className="text-center py-2 px-2 font-mono font-semibold">
                        {entry?.total_hours != null ? `${entry.total_hours.toFixed(1)}h` : '—'}
                      </td>
                      <td className="text-center py-2 px-2">
                        {entry?.admin_edited ? (
                          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-blue-100 text-blue-700" data-testid={`corrected-badge-${emp.id}`}>corrigé</span>
                        ) : entry?.clock_in_location ? (
                          <MapPin className="w-4 h-4 text-emerald-500 mx-auto" />
                        ) : entry?.clock_in ? (
                          <AlertTriangle className="w-4 h-4 text-red-500 mx-auto" />
                        ) : (
                          <span className="text-slate-300">—</span>
                        )}
                      </td>
                      <td className="text-center py-2 px-2">
                        {isEditing ? (
                          <div className="flex gap-1 justify-center">
                            <Button size="sm" variant="ghost" onClick={() => saveEdit(emp.id)} disabled={saving} data-testid="save-edit-btn">
                              <Save className="w-4 h-4 text-emerald-600" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => setEditingEntry(null)} data-testid="cancel-edit-btn">
                              <X className="w-4 h-4 text-slate-400" />
                            </Button>
                          </div>
                        ) : (
                          <Button size="sm" variant={entry ? 'ghost' : 'outline'} onClick={() => setDialogEmp(emp)} data-testid={`edit-entry-${emp.id}`}>
                            {entry ? <><Edit3 className="w-4 h-4 text-slate-500 mr-1" />Corriger</> : <><Plus className="w-4 h-4 mr-1" />Ajouter</>}
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}
      {dialogEmp && <TimeEntryEditDialog emp={dialogEmp} entry={getEntryForEmployee(dialogEmp.id)} date={selectedDate} onClose={() => setDialogEmp(null)} onSaved={() => { setDialogEmp(null); loadData(); }} />}

      {/* === SCHEDULE TAB === */}
      {tab === 'schedule' && schedule && (
        <Card className="p-6 rounded-xl border border-slate-200">
          <h3 className="text-lg font-bold text-slate-900 mb-4">Horaires par role</h3>
          <div className="space-y-6">
            {['atelier', 'reception', 'vente'].map(role => (
              <div key={role} className="p-4 bg-slate-50 rounded-lg">
                <h4 className="font-semibold text-slate-700 capitalize mb-3">{role}</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div>
                    <label className="text-xs text-slate-500">Matin debut</label>
                    <Input type="time" value={schedule[role]?.morning?.start || ''} onChange={e => setSchedule(s => ({...s, [role]: {...s[role], morning: {...s[role].morning, start: e.target.value}}}))} className="mt-1" data-testid={`schedule-${role}-morning-start`} />
                  </div>
                  <div>
                    <label className="text-xs text-slate-500">Matin fin</label>
                    <Input type="time" value={schedule[role]?.morning?.end || ''} onChange={e => setSchedule(s => ({...s, [role]: {...s[role], morning: {...s[role].morning, end: e.target.value}}}))} className="mt-1" data-testid={`schedule-${role}-morning-end`} />
                  </div>
                  <div>
                    <label className="text-xs text-slate-500">Apres-midi debut</label>
                    <Input type="time" value={schedule[role]?.afternoon?.start || ''} onChange={e => setSchedule(s => ({...s, [role]: {...s[role], afternoon: {...s[role].afternoon, start: e.target.value}}}))} className="mt-1" data-testid={`schedule-${role}-afternoon-start`} />
                  </div>
                  <div>
                    <label className="text-xs text-slate-500">Apres-midi fin</label>
                    <Input type="time" value={schedule[role]?.afternoon?.end || ''} onChange={e => setSchedule(s => ({...s, [role]: {...s[role], afternoon: {...s[role].afternoon, end: e.target.value}}}))} className="mt-1" data-testid={`schedule-${role}-afternoon-end`} />
                  </div>
                </div>
                <div className="mt-3">
                  <label className="text-xs text-slate-500">Pause max (minutes)</label>
                  <Input type="number" value={schedule[role]?.break_minutes || 20} onChange={e => setSchedule(s => ({...s, [role]: {...s[role], break_minutes: parseInt(e.target.value) || 20}}))} className="mt-1 w-24" data-testid={`schedule-${role}-break`} />
                </div>
              </div>
            ))}

            <div className="p-4 bg-slate-50 rounded-lg">
              <label className="text-xs text-slate-500">Coupure automatique (heure)</label>
              <Input type="number" min={17} max={23} value={schedule.cutoff_hour || 20} onChange={e => setSchedule(s => ({...s, cutoff_hour: parseInt(e.target.value) || 20}))} className="mt-1 w-24" data-testid="schedule-cutoff" />
              <p className="text-xs text-slate-400 mt-1">Aucun timbrage possible apres cette heure</p>
            </div>

            <Button onClick={saveSchedule} disabled={saving} data-testid="save-schedule-btn">
              <Save className="w-4 h-4 mr-1" /> {saving ? 'Sauvegarde...' : 'Sauvegarder les horaires'}
            </Button>
          </div>
        </Card>
      )}

      {/* === LOGS TAB === */}
      {tab === 'logs' && (
        <Card className="p-6 rounded-xl border border-slate-200">
          <h3 className="text-lg font-bold text-slate-900 mb-4">Logs de securite - {formatDate(selectedDate)}</h3>
          <div className="space-y-2">
            {logs.length === 0 ? (
              <p className="text-slate-400 text-sm">Aucun log pour cette date</p>
            ) : logs.map((log, i) => {
              const emp = employees.find(e => e.id === log.employee_id);
              return (
                <div key={i} className={`flex items-center gap-3 p-2 rounded-lg text-sm ${log.status === 'rejected' ? 'bg-red-50 border border-red-200' : 'bg-slate-50'}`} data-testid={`log-${i}`}>
                  <span className={`px-2 py-0.5 rounded text-xs font-bold ${log.status === 'rejected' ? 'bg-red-200 text-red-800' : 'bg-emerald-200 text-emerald-800'}`}>
                    {log.status === 'rejected' ? 'REJETE' : 'OK'}
                  </span>
                  <span className="font-medium">{emp ? `${emp.first_name} ${emp.last_name}` : log.employee_id}</span>
                  <span className="text-slate-500">{log.action_type}</span>
                  <span className="font-mono text-xs text-slate-400">{new Date(log.timestamp).toLocaleTimeString('fr-FR')}</span>
                  {log.distance_meters > 0 && (
                    <span className="text-xs text-slate-400">{Math.round(log.distance_meters)}m</span>
                  )}
                  {log.reason && <span className="text-xs text-red-600 ml-auto">{log.reason}</span>}
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* === IPs TAB === */}
      {tab === 'ips' && (
        <Card className="p-6 rounded-xl border border-slate-200">
          <h3 className="text-lg font-bold text-slate-900 mb-2">Adresses IP autorisees</h3>
          <p className="text-sm text-slate-500 mb-4">
            Seuls les postes connectes depuis ces IPs peuvent afficher le QR code de timbrage.
            Le QR code est invisible depuis les telephones des employes.
          </p>
          <div className="space-y-3">
            {allowedIps.map((ip, i) => (
              <div key={i} className="flex items-center gap-2">
                <Input value={ip} readOnly className="font-mono flex-1" />
                <Button variant="ghost" size="sm" onClick={() => { setAllowedIps(prev => prev.filter((_, j) => j !== i)); }} className="text-red-500 hover:text-red-700" data-testid={`remove-ip-${i}`}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ))}
            <div className="flex items-center gap-2">
              <Input placeholder="Ex: 178.195.239.144" value={newIp} onChange={e => setNewIp(e.target.value)} className="font-mono flex-1" data-testid="new-ip-input" />
              <Button size="sm" onClick={() => { if (newIp.trim()) { setAllowedIps(prev => [...prev, newIp.trim()]); setNewIp(''); } }} data-testid="add-ip-btn">{t('common.add')}</Button>
            </div>
            <Button onClick={saveIps} className="mt-2" data-testid="save-ips-btn">
              <Save className="w-4 h-4 mr-1" /> Sauvegarder les IPs
            </Button>
          </div>
        </Card>
      )}
    </div>
    </AdminLayout>
  );
};

export default AdminTimeclock;
